import classNames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { differenceInDays, differenceInHours, differenceInMinutes } from 'date-fns';

import { PropsWithClassname } from '@/app/types';
import { ProjectSettingsModeEnum, ProjectStatistics as ProjectStatisticsType, ProjectTimestamps } from '@/app/api';
import { formatNumber, formatPriceInCents } from '@/app/utils/formatting';

interface ProjectStatisticsProps extends PropsWithClassname {
  // TODO this component should be able to display SubprojectStatistics as well, as soon its finished - https://doniocz.atlassian.net/browse/DON-2719
  statistics: ProjectStatisticsType;
  timestamps: ProjectTimestamps;
  projectMode: ProjectSettingsModeEnum;
}

const ProjectStatistics = ({ statistics, projectMode, timestamps, className }: ProjectStatisticsProps) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const contributorsStatistics = useMemo(() => {
    if ([ProjectSettingsModeEnum.DonioPlus as string].includes(projectMode)) {
      return t('components.ProjectStatistics.projectContributorsInfo', {
        count: statistics.donationsCount ?? 0,
        countFormatted: formatNumber(statistics.donationsCount ?? 0, language),
      });
    }

    return t('components.ProjectStatistics.charityCollectionContributorsInfo', {
      count: statistics.donationsCount ?? 0,
      countFormatted: formatNumber(statistics.donationsCount ?? 0, language),
    });
  }, [projectMode, statistics, t]);

  const averageContributionFootnote = useMemo(() => {
    if ([ProjectSettingsModeEnum.DonioPlus as string].includes(projectMode)) {
      return t('components.ProjectStatistics.averageContributionFootnote');
    }

    return t('components.ProjectStatistics.averageDonationFootnote');
  }, [projectMode, t]);

  const largestContributionFootnote = useMemo(() => {
    if ([ProjectSettingsModeEnum.DonioPlus as string].includes(projectMode)) {
      return t('components.ProjectStatistics.largestContributionFootnote');
    }

    return t('components.ProjectStatistics.largestDonationFootnote');
  }, [projectMode, t]);

  const durationStatistics = useMemo(() => {
    if (!timestamps.startedAt || !timestamps.finishedAt) {
      return;
    }

    const daysRemaining = differenceInDays(timestamps.finishedAt, timestamps.startedAt);
    const hoursRemaining = differenceInHours(timestamps.finishedAt, timestamps.startedAt);
    const minutesRemaining = differenceInMinutes(timestamps.finishedAt, timestamps.startedAt);

    if (minutesRemaining < 60) {
      return t('components.ProjectStatistics.durationInMinutes', { count: minutesRemaining });
    }

    if (hoursRemaining < 24) {
      return t('components.ProjectStatistics.durationInHours', { count: hoursRemaining });
    }

    return t('components.ProjectStatistics.durationInDays', { count: daysRemaining });
  }, [timestamps, t]);

  return (
    <div className={classNames(className, '')}>
      <div className="flex justify-between align-center mb-[40px] lg:mb-[64px]">
        <div className="w-[45%]">
          <span className="block text-[20px] lg:text-[24px] leading-[24px] lg:leading-[32px] font-medium">
            {contributorsStatistics}
          </span>
          <span className="block text-[14px] lg:text-[16px] leading-[20px] lg:leading-[24px] whitespace-nowrap">
            {t('components.ProjectStatistics.contributorsFootnote')}
          </span>
        </div>
        <div className="w-[45%]">
          <span className="block text-[20px] lg:text-[24px] leading-[24px] lg:leading-[32px] font-medium">
            {formatPriceInCents(statistics.averageDonationInCents ?? 0)}
          </span>
          <span className="block text-[14px] lg:text-[16px] leading-[20px] lg:leading-[24px]">
            {averageContributionFootnote}
          </span>
        </div>
      </div>
      <div className="flex justify-between align-center mb-[40px] lg:mb-[64px]">
        <div className="w-[45%]">
          <span className="block text-[20px] lg:text-[24px] leading-[24px] lg:leading-[32px] font-medium">
            {formatPriceInCents(statistics.largestDonationInCents ?? 0)}
          </span>
          <span className="block text-[14px] lg:text-[16px] leading-[20px] lg:leading-[24px]">
            {largestContributionFootnote}
          </span>
        </div>
        <div className="w-[45%]">
          <span className="block text-[20px] lg:text-[24px] leading-[24px] lg:leading-[32px] font-medium">
            {durationStatistics}
          </span>
          <span className="block text-[14px] lg:text-[16px] leading-[20px] lg:leading-[24px]">
            {t('components.ProjectStatistics.durationFootnote')}
          </span>
        </div>
      </div>
    </div>
  );
};

export default ProjectStatistics;
